import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import HelpButton from '../components/HelpButton';

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      const data = await api.getUsers();
      setUsers(data);
    } catch (err) {
      setError('Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  function startEdit(user) {
    setEditing(user);
    setShowForm(true);
  }

  function startCreate() {
    setEditing(null);
    setShowForm(true);
  }

  async function handleDeactivate(user) {
    if (!window.confirm(`¿Desactivar a ${user.display_name}?`)) return;
    try {
      await api.deleteUser(user.id);
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  }

  if (loading) return <div style={styles.loading}>Cargando usuarios... 🎄</div>;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h2 style={styles.title}>👥 Usuarios</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <HelpButton title="👥 Gestión de usuarios">
            <p>Desde aquí creas y editas a los miembros de la familia que usarán la app.</p>
            <p>Cada persona necesita un <strong>usuario</strong> y una <strong>contraseña</strong> para entrar.</p>
            <p>Las <strong>incompatibilidades</strong> son para marcar parejas (matrimonios, novios...) que no deben tocarse entre sí en el sorteo del amigo invisible.</p>
            <p>Puedes <strong>desactivar</strong> a alguien sin borrar sus datos, por ejemplo si deja de participar un año.</p>
          </HelpButton>
          <button className="btn btn-primary" onClick={startCreate}>+ Nuevo usuario</button>
        </div>
      </div>

      {error && <p className="error-msg" style={{ marginBottom: '1rem' }}>⚠ {error}</p>}

      <div style={styles.list}>
        {users.map(user => (
          <div key={user.id} className="card" style={styles.userCard}>
            <div style={styles.userInfo}>
              <div style={styles.userName}>
                {user.display_name}
                {user.is_admin && <span className="badge badge-admin" style={{ marginLeft: '0.5rem' }}>Admin</span>}
                {!user.is_active && <span className="badge badge-inactive" style={{ marginLeft: '0.5rem' }}>Inactivo</span>}
              </div>
              <div style={styles.userMeta}>@{user.username}</div>
              {user.incompatibilities?.length > 0 && (
                <div style={styles.incompatNote}>
                  ⚠ Incompatible con {user.incompatibilities.length} persona(s)
                </div>
              )}
            </div>
            <div style={styles.userActions}>
              <button className="btn btn-ghost" onClick={() => startEdit(user)}>Editar</button>
              {user.is_active && !user.is_admin && (
                <button className="btn btn-danger" onClick={() => handleDeactivate(user)}>Desactivar</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <UserFormModal
          user={editing}
          allUsers={users}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); loadUsers(); }}
        />
      )}
    </div>
  );
}

function UserFormModal({ user, allUsers, onClose, onSaved }) {
  const isEditing = !!user;
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [username, setUsername] = useState(user?.username || '');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(user?.is_admin || false);
  const [isActive, setIsActive] = useState(user?.is_active ?? true);
  const [incompatibilities, setIncompatibilities] = useState(user?.incompatibilities || []);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  function toggleIncompat(id) {
    setIncompatibilities(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }

  async function handleSave() {
    setError('');
    setSaving(true);
    try {
      if (isEditing) {
        const payload = { display_name: displayName, is_admin: isAdmin, is_active: isActive, incompatibilities };
        if (password) payload.password = password;
        await api.updateUser(user.id, payload);
      } else {
        if (!username || !password || !displayName) throw new Error('Rellena todos los campos obligatorios');
        await api.createUser({ username, password, display_name: displayName, is_admin: isAdmin, incompatibilities });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const otherUsers = allUsers.filter(u => u.id !== user?.id);

  return (
    <div style={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h3 style={{ fontFamily: 'var(--font-display)', color: 'var(--gold)' }}>
            {isEditing ? `Editar: ${user.display_name}` : 'Nuevo usuario'}
          </h3>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.modalBody}>
          <div style={styles.field}>
            <label className="label">Nombre completo *</label>
            <input className="input" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="María García" />
          </div>

          {!isEditing && (
            <div style={styles.field}>
              <label className="label">Usuario (para iniciar sesión) *</label>
              <input className="input" value={username} onChange={e => setUsername(e.target.value)} placeholder="maria.garcia" autoCapitalize="none" />
            </div>
          )}

          <div style={styles.field}>
            <label className="label">{isEditing ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}</label>
            <input className="input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
          </div>

          <div style={styles.checkRow}>
            <label style={styles.checkLabel}>
              <input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} />
              <span>Es administrador</span>
            </label>
            {isEditing && (
              <label style={styles.checkLabel}>
                <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                <span>Activo</span>
              </label>
            )}
          </div>

          {otherUsers.length > 0 && (
            <div style={styles.field}>
              <label className="label">Incompatibilidades (pareja, etc.)</label>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                Las personas marcadas no podrán regalarse entre sí
              </p>
              <div style={styles.incompatList}>
                {otherUsers.map(u => (
                  <label key={u.id} style={styles.checkLabel}>
                    <input
                      type="checkbox"
                      checked={incompatibilities.includes(u.id)}
                      onChange={() => toggleIncompat(u.id)}
                    />
                    <span>{u.display_name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {error && <p className="error-msg">⚠ {error}</p>}
        </div>

        <div style={styles.modalFooter}>
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear usuario'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { padding: '1rem' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  title: { fontSize: '1.4rem', color: 'var(--gold)' },
  list: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  loading: { textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' },
  userCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
  },
  userInfo: { flex: 1 },
  userName: { fontWeight: 600, fontSize: '1rem', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.25rem' },
  userMeta: { color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.2rem' },
  incompatNote: { color: 'var(--gold)', fontSize: '0.78rem', marginTop: '0.3rem' },
  userActions: { display: 'flex', gap: '0.5rem', flexShrink: 0, flexWrap: 'wrap' },
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    zIndex: 1000,
    padding: '0',
  },
  modal: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '16px 16px 0 0',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid var(--border)',
  },
  closeBtn: {
    background: 'none', color: 'var(--text-muted)',
    fontSize: '1.2rem', cursor: 'pointer',
  },
  modalBody: {
    padding: '1.5rem',
    overflowY: 'auto',
    display: 'flex', flexDirection: 'column', gap: '1rem',
    flex: 1,
  },
  modalFooter: {
    padding: '1rem 1.5rem',
    borderTop: '1px solid var(--border)',
    display: 'flex', justifyContent: 'flex-end', gap: '0.75rem',
  },
  field: { display: 'flex', flexDirection: 'column' },
  checkRow: { display: 'flex', gap: '1.5rem' },
  checkLabel: {
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    cursor: 'pointer', fontSize: '0.9rem', color: 'var(--text)',
  },
  incompatList: {
    display: 'flex', flexDirection: 'column', gap: '0.5rem',
    background: 'var(--bg)', borderRadius: '8px', padding: '0.75rem',
    border: '1px solid var(--border)',
  },
};
